import React, { useEffect } from 'react';

const WIDTH = {
  md: 'max-w-sm',
  lg: 'max-w-md',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  '7xl': 'max-w-7xl',
};

/**
 * Slide-in filter panel from the right. Less “floating dialog”, more
 * app-style inspector: full height, clear sections, sticky footer.
 */
export function FilterDrawer({
  open,
  onClose,
  eyebrow,
  title,
  description,
  headerActions,
  width = 'lg',
  children,
  footer,
  /** Optional id for aria-labelledby */
  titleId = 'filter-drawer-title',
}) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const w = WIDTH[width] || WIDTH.lg;

  return (
    <div className="fixed inset-0 z-[80] flex justify-end" role="presentation">
      <button
        type="button"
        className="absolute inset-0 bg-slate-950/45 backdrop-blur-[2px] animate-fade-in"
        onClick={onClose}
        aria-label="Close filters"
      />
      <div
        className={`
          relative flex h-full w-full max-h-dvh min-h-0 ${w}
          flex-col overflow-hidden
          border-l border-slate-200/90 bg-white shadow-[-8px_0_40px_-10px_rgba(15,23,42,0.18)]
          animate-slide-in-right
        `}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 border-b border-slate-100 bg-gradient-to-b from-slate-50/80 to-white px-3 py-2.5 sm:px-5 sm:py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 pr-2">
              {eyebrow && (
                <p className="text-[0.65rem] font-bold uppercase tracking-[0.18em] text-primary-700">{eyebrow}</p>
              )}
              {title && (
                <h2
                  id={titleId}
                  className={`text-base font-bold tracking-tight text-slate-900 sm:text-lg ${eyebrow ? 'mt-0.5' : ''}`}
                >
                  {title}
                </h2>
              )}
              {description && (
                <p className="mt-0.5 text-sm leading-relaxed text-slate-500">{description}</p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {headerActions}
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
                aria-label="Close"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-3 py-3 sm:px-5 sm:py-4 [scrollbar-gutter:stable]">
          {children}
        </div>

        {footer && (
          <div className="shrink-0 border-t border-slate-200/80 bg-slate-50/90 px-4 py-3 shadow-[0_-4px_24px_-2px_rgba(15,23,42,0.06)] backdrop-blur sm:px-6 sm:py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Grouped block inside a filter drawer (card-style section).
 */
export function FilterFormSection({ title, hint, children, className = '' }) {
  return (
    <section
      className={`
        rounded-2xl border border-slate-200/80 bg-slate-50/35 p-4
        ring-1 ring-slate-900/[0.03] sm:p-5
        ${className}
      `.trim()}
    >
      <div className="mb-3 sm:mb-4">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        {hint && <p className="mt-0.5 text-xs leading-relaxed text-slate-500">{hint}</p>}
      </div>
      <div className="space-y-3.5 sm:space-y-4">{children}</div>
    </section>
  );
}

export default FilterDrawer;
